import { useMemo } from 'react';
import PixelBackground from '@/components/PixelBackground';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import Display from '@/components/typography/Display';
import Button from '@/components/ui/Button';
import { Container, Section } from '@/components/ui/Section';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocalizedPath } from '@/lib/locale';
import Seo from '@/components/Seo';
import { BASE_URL } from '@/seo/meta';
import { CONTACT } from '@/config/contact';

export default function ConsultationThankYouPage() {
  const { language } = useLanguage();
  const localizedPath = useLocalizedPath();
  const isGreek = language === 'el';

  const copy = useMemo(
    () =>
      isGreek
        ? {
            eyebrow: 'Αίτημα εστάλη',
            title: 'Ευχαριστούμε!',
            subtitle: 'Η αίτησή σας για δωρεάν συμβουλευτική έχει σταλεί επιτυχώς.',
            followUp: 'Θα επικοινωνήσουμε μαζί σας εντός 24 ωρών για να προγραμματίσουμε τη συνάντησή μας.',
            backHome: 'Επιστροφή στην αρχική',
            contactLabel: 'Ή επικοινωνήστε απευθείας:',
          }
        : {
            eyebrow: 'Request sent',
            title: 'Thank you!',
            subtitle: 'Your free consultation request has been sent successfully.',
            followUp: 'We will contact you within 24 hours to schedule the call.',
            backHome: 'Back to home',
            contactLabel: 'Or contact us directly:',
          },
    [isGreek],
  );

  const seoMeta = useMemo(() => {
    if (isGreek) {
      return {
        title: 'Ευχαριστούμε | PixelPro Solutions',
        description: 'Η αίτησή σας για δωρεάν συμβουλευτική έχει σταλεί επιτυχώς.',
        canonical: `${BASE_URL}/consultation/thank-you`,
      };
    }
    return {
      title: 'Thank You | PixelPro Solutions',
      description: 'Your free consultation request has been sent successfully.',
      canonical: `${BASE_URL}/en/consultation/thank-you`,
    };
  }, [isGreek]);

  return (
    <>
      <Seo
        title={seoMeta.title}
        description={seoMeta.description}
        url={seoMeta.canonical}
        noindex
      />

      <div className="relative min-h-screen bg-[#080c0d] text-white">
        <PixelBackground />
        <Header variant="simple" />

        <main id="main-content" className="relative z-10">
          <Section className="relative overflow-hidden pt-28 lg:pt-32">
            <div className="absolute inset-0 geometric-pattern" aria-hidden="true" />
            <div className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black" aria-hidden="true" />
            <Container className="relative max-w-4xl">
              <div className="rounded-[32px] border border-white/15 bg-black/70 backdrop-blur-xl p-8 md:p-12 shadow-glass text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-brand-highlight/10 via-transparent to-white/5 pointer-events-none" aria-hidden="true" />
                <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-brand-highlight/20 blur-3xl" aria-hidden="true" />
                <div className="pointer-events-none absolute -left-24 bottom-0 h-56 w-56 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
                <div className="relative">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.4em] text-brand-highlight/80 mb-4">
                    {copy.eyebrow}
                  </p>
                  <Display
                    variant="h1"
                    as="h1"
                    className="text-white text-3xl md:text-4xl lg:text-5xl leading-[1.15] mb-4"
                  >
                    {copy.title}
                  </Display>
                  <p className="text-white/70 text-lg mb-2">
                    {copy.subtitle}
                  </p>
                  <p className="text-white/55 mb-8">
                    {copy.followUp}
                  </p>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                    <Button
                      as="a"
                      href={localizedPath('/')}
                      variant="primary"
                      size="lg"
                      className="justify-center"
                    >
                      {copy.backHome}
                    </Button>
                  </div>

                  <div className="text-sm text-white/65">
                    <p>{copy.contactLabel}</p>
                    <p className="mt-2 space-x-2">
                      <a href={`mailto:${CONTACT.email}`} className="text-white hover:text-brand-highlight">
                        {CONTACT.email}
                      </a>
                      <span>|</span>
                      <a href={CONTACT.phoneHref} className="text-white hover:text-brand-highlight">
                        {CONTACT.phoneDisplay}
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </Container>
          </Section>
        </main>

        <Footer variant="compact" />
      </div>
    </>
  );
}
